




































































































































import {
  ComponentInternalInstance,
  computed,
  defineComponent,
  getCurrentInstance,
  reactive,
  ref,
  toRefs,
  onMounted,
  onUnmounted,
} from '@vue/composition-api';
import { ErrorCircleFilledIcon } from 'tdesign-icons-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import { IpcEvents } from '@/ipc-events';
import { IS_DEV, IS_TEST, IS_PRE, IS_PC_IN_LINUX } from '@/common/constant/platform';
import ipc from '@/renderer/scripts/utils/ipc';
import tips from '@/renderer/scripts/utils/tips';
import { compareVersion, formatSize } from '@/renderer/scripts/utils/tools';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import storage from '@/renderer/scripts/utils/storage';
import { httpUrl } from '@/renderer/scripts/http';

interface UpdateData {
  files: Array<{ sha512: string; size: number; url: string }>;
  path: string;
  releaseDate: string;
  sha512: string;
  version: string;
  features?: {
    'zh-CN': string[];
    'en-US': string[];
  };
  bugfixs?: {
    'zh-CN': string[];
    'en-US': string[];
  };
}

export default defineComponent({
  components: {
    ErrorCircleFilledIcon,
  },
  setup() {
    // 当前组件
    const currComponent = getCurrentInstance() as ComponentInternalInstance;
    const vm = currComponent.proxy;
    const store = vm.$store;
    const publicDir =
      window.location.search.includes('beta') || IS_DEV || IS_TEST || IS_PRE ? '/api/upgrade/beta/' : '/api/upgrade/';
    // 私有化下请求public/releases
    const privateDir = '/releases/';
    const updateDir = store.getters.isPrivate ? privateDir : publicDir;
    const updateUrl = ref(httpUrl.webUrl + updateDir);
    let timer: any = null;
    const downloadState = reactive({
      completed: false,
      transferred: '0B',
      speed: '0B/s',
      total: '0B',
      progress: 0,
      isDownloading: false,
    });
    const showUpdateNow = ref(false);
    const showForceUpdate = ref(false); // 需要进行强制更新的弹窗
    const isForceUpdate = ref(false); // 需要进行强制更新
    const showManualDownload = ref(false); // 手动下载更新弹窗
    const showProgress = ref(false);
    const updateInfo = ref<UpdateData | null>(null);
    const autoUpdate = ref(false);
    const importantUpdate = ref(false);
    const showRestartBtn = ref(false);
    const setMyInterval = (func: Function, wait: number) => {
      const interv = () => {
        func.call(null);
        timer = setTimeout(interv, wait);
      };
      timer = setTimeout(interv, wait);
    };
    let showProgressAndVisibleInstall = false;
    const methods = {
      async checkForUpdate(activeByUser?: boolean) {
        const params = {
          updateUrl: updateUrl.value,
        };
        try {
          vm.$store.commit('SET_UPDATE_LOADING', true);
          const data = await ipc.invoke(IpcEvents.INVOKE_OPERATE_MAIN_LISTENER, {
            event: 'checkForUpdate',
            params,
          });
          vm.$store.commit('SET_UPDATE_LOADING', false);
          // 取不到最新版本号
          if (!data.version) {
            // 远端服务器连接失败，取不到更新版本
            showProgressAndVisibleInstall && tips.error(i18n_t('获取更新失败'));
            return;
          }
          updateInfo.value = data;

          // 是否有更新
          const remoteVersion = data.version;
          vm.$store.commit('SET_LATEST_VERSION', remoteVersion);
          const { updateLevel } = vm.$store.getters;
          if (updateLevel <= 0) {
            if (activeByUser) {
              tips.warning(i18n_t('当前版本已是最新版本'));
            }
            return;
          }
          try {
            // 获取远端强更版本配置
            await vm.$store.dispatch('GetForceUpdateSettings');
          } catch (error) {
            // 获取强更失败则不做任何处理，默认没有版本需要强更
          }
          methods.handleUpdate(activeByUser);
        } catch (error) {
          if (showProgressAndVisibleInstall) {
            tips.error(i18n_t('获取更新失败'));
          } else {
            logger.ui('[update] 获取更新失败', JSON.stringify(error));
          }
        }
      },
      handleUpdate(triger?: boolean) {
        const { currVersion, updateLevel, forceUpdate } = vm.$store.getters;
        const { version: remoteVersion } = updateInfo.value || { version: '0' };
        // 当前版本号小于等于需要强升的版本号就需要强升
        const needForceUpdate = compareVersion(currVersion, forceUpdate.needToUpdate) >= 0;
        if (triger && (updateLevel === 1 || updateLevel === 2)) {
          // 这里不考虑updateLevel为0的情况，因为在调用handleUpdate之前已被拦截
          showUpdateNow.value = true;
        } else if (forceUpdate.versions.includes(currVersion)) {
          // 当前版本有重大 BUG，需要强制升级
          isForceUpdate.value = true;
          showForceUpdate.value = true;
        } else if (needForceUpdate) {
          // 当前版本过旧，需要强制升级
          isForceUpdate.value = true;
          showForceUpdate.value = true;
          importantUpdate.value = true;
        } else if (updateLevel === 1 || updateLevel === 2) {
          const localMajorVersion = storage.get<string>(storage.StoreKey.MAJOR_VERSION) || '0';
          // 大版本，弹窗更新，仅弹一次
          const bigVersionLevel = compareVersion(localMajorVersion, remoteVersion);
          (localMajorVersion === '0' || bigVersionLevel === 1 || bigVersionLevel === 2) && (showUpdateNow.value = true);
          storage.set(storage.StoreKey.MAJOR_VERSION, remoteVersion);
          if (autoUpdate.value) {
            // 如果为 BUG 修复型更新且允许自动更新，直接下载更新
            methods.downloadUpdate(true);
          }
        } else if (autoUpdate.value) {
          // 如果有更新，并且允许自动更新，直接下载更新
          methods.downloadUpdate(true);
        } else {
          // 什么都不做，等待用户点击更新
        }
      },
      /**
       * @description 显式更新
       * @param {Boolean} silentUpdate 是否静默更新
       */
      explicitUpdate() {
        storage.set(storage.StoreKey.MAJOR_VERSION, '0');
        showUpdateNow.value = false;
        showForceUpdate.value = false;
        // 不静默更新更新
        methods.downloadUpdate(false);
      },
      /**
       * @description 隐式更新（后台更新）或忽略当前版本
       */
      implicitUpdate() {
        if (autoUpdate.value) {
          storage.set(storage.StoreKey.MAJOR_VERSION, '0');
          showUpdateNow.value = false;
          showForceUpdate.value = false;
          methods.downloadUpdate(true);
        } else {
          showUpdateNow.value = false;
          showForceUpdate.value = false;
        }
      },
      /**
       * @description 下载更新
       * @param {Boolean} silentUpdate 是否静默更新
       */
      downloadUpdate(silentUpdate: boolean) {
        const { hasNewVersion, updateLoading } = vm.$store.getters;
        if (!hasNewVersion && downloadState.isDownloading && updateLoading) return;
        downloadState.progress = 0;
        downloadState.completed = false;
        downloadState.isDownloading = true;
        showProgressAndVisibleInstall = !silentUpdate;
        showProgress.value = !silentUpdate;
        const params = {
          updateUrl: updateUrl.value,
          visibleInstall: !silentUpdate,
        };

        // 触发更新进度通知
        ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
          event: 'onUpdateProgress',
        });
        ipc
          .invoke(IpcEvents.INVOKE_OPERATE_MAIN_LISTENER, {
            event: 'downloadUpdate',
            params,
          })
          .then(data => {
            if (data.error) {
              showProgress.value = false;
              if (!silentUpdate || isForceUpdate.value) {
                // 若强制更新失败，则弹窗手动下载更新
                showManualDownload.value = true;
              }
            } else {
              downloadState.progress = 100;
              downloadState.completed = true;
              !silentUpdate && tips.success(i18n_t('下载更新成功'));
              if (showProgress.value) {
                // 等2秒升级自动重启异常才出现这个按钮
                setTimeout(() => {
                  showRestartBtn.value = true;
                }, 2000);
              }
            }
          });
      },
      onUpdateProgress(e: any, progressInfo: any) {
        const { size: speedSize, unit: speedUnit } = formatSize(progressInfo.bytesPerSecond);
        downloadState.speed = `${speedSize} ${speedUnit}/s`;
        const { size: total, unit: totalUnit } = formatSize(progressInfo.total);
        downloadState.total = `${total} ${totalUnit}`;
        const { size: transferred, unit: transferredUnit } = formatSize(progressInfo.transferred);
        downloadState.transferred = `${transferred} ${transferredUnit}`;
        downloadState.progress = +progressInfo.percent.toFixed(2);
        downloadState.completed = false;
        showProgress.value = showProgressAndVisibleInstall;
      },
      onManualDownload(e: Event) {
        e.preventDefault();
        const host = store.getters.serverPrefix + store.getters.serverSuffix;
        dispatchRouter(`${host}/download`);
      },
      onRestart() {
        // 这里主要给用户重启的方法，解决Mac下出现升级无法自动重启问题
        ipc.send(IpcEvents.RESTART);
        logger.ui('restart click');
      },
    };
    const updateFunc = (activeByUser?: boolean) => {
      const systemSetting: any = storage.get(storage.StoreKey.SYSTEM_SETTING);
      autoUpdate.value = systemSetting.autoUpdate;
      methods.checkForUpdate(activeByUser);
    };
    const checkUpdateByUser = () => {
      updateFunc(true);
    };

    /** 监听私有化域名的变化更新升级url */
    const watchInternalUrlChanged = () => {
      updateUrl.value = httpUrl.webUrl + updateDir;
    };
    setTimeout(async () => {
      updateFunc();
    }, 1000);
    onMounted(() => {
      // 每两小时自动检查一次，是否有更新
      setMyInterval(() => {
        updateFunc();
      }, 7200000);
      ipc.on(ipc.RENDERER_EVENTS_MAP.CHECK_UPDATE, checkUpdateByUser); // 监听用户手动的检查更新
      ipc.on(ipc.RENDERER_EVENTS_MAP.UPDATE_NOW, methods.explicitUpdate); // 从设置中点击更新
      ipc.on(IpcEvents.UPGRADE_DOWNLOAD_PROGRESS, methods.onUpdateProgress);
      ipc.on(ipc.RENDERER_EVENTS_MAP.UPGRADE_PC_NOW, methods.downloadUpdate);
      httpUrl.on(httpUrl.EVENT_MAP.SMHHostChanged, watchInternalUrlChanged);
    });
    onUnmounted(() => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      ipc.off(ipc.RENDERER_EVENTS_MAP.CHECK_UPDATE, checkUpdateByUser);
      ipc.off(ipc.RENDERER_EVENTS_MAP.UPDATE_NOW, methods.explicitUpdate); // 从设置中点击更新
      ipc.off(IpcEvents.UPGRADE_DOWNLOAD_PROGRESS, methods.onUpdateProgress);
      ipc.off(ipc.RENDERER_EVENTS_MAP.UPGRADE_PC_NOW, methods.downloadUpdate);
      httpUrl.off(httpUrl.EVENT_MAP.SMHHostChanged, watchInternalUrlChanged);
    });
    return {
      IS_PC_IN_LINUX,
      showUpdateNow,
      showForceUpdate,
      showManualDownload,
      showProgress,
      autoUpdate,
      importantUpdate,
      updateInfo,
      ...toRefs(downloadState),
      ...methods,
      showRestartBtn,
    };
  },
});
